import {
  Button,
  FormControl,
  Grid2 as Grid,
  OutlinedInput,
  Select,
  MenuItem,
  ListItemText,
  Box,
  Chip,
  Paper,
  InputAdornment,
  Tooltip,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  Tune as TuneIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";

/**
 * Prompt Template 필터 오가니즘 Props
 */
export type FilterProps = {
  /**
   * 현재 필터 값들
   */
  values: {
    category: string;
    keyword: string;
  };
  /**
   * 사용 가능한 카테고리 목록
   */
  categories: string[];
  /**
   * 필터 변경 이벤트
   */
  onChange: (values: {
    category: string;
    keyword: string;
  }) => void;
  /**
   * 필터 초기화 이벤트
   */
  onReset: () => void;
};

/**
 * Prompt Template 필터 오가니즘
 *
 * @description SuperChatContent Filter를 참고하여 구현
 * @param props FilterProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const Filter = (props: FilterProps) => {
  const { values, categories, onChange, onReset } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [formValues, setFormValues] = useState({
    category: values.category || "",
    keyword: values.keyword || "",
  });

  // props가 변경될 때 formValues 업데이트
  useEffect(() => {
    setFormValues({
      category: values.category || "",
      keyword: values.keyword || "",
    });
  }, [values]);

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    onChange(formValues);
    handleCloseFilters();
  };

  const handleSearch = () => {
    onChange(formValues);
  };

  const handleReset = () => {
    const defaultValues = {
      category: "",
      keyword: "",
    };
    setFormValues(defaultValues);
    onReset();
    handleCloseFilters();
  };

  const handleKeywordChange = (value: string) => {
    setFormValues({ ...formValues, keyword: value });
  };

  const handleCategoryChange = (value: string) => {
    setFormValues({ ...formValues, category: value });
  };

  const open = Boolean(anchorEl);
  const id = open ? "filters-popover" : undefined;

  // 활성화된 필터 태그 생성
  const renderFilterTags = () => {
    const tags = [];

    if (formValues.keyword) {
      tags.push(
        <Chip
          key="keyword"
          label={`Search: ${formValues.keyword}`}
          size="small"
          onDelete={() => handleKeywordChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.category) {
      tags.push(
        <Chip
          key="category"
          label={`Category: ${formValues.category}`}
          size="small"
          onDelete={() => handleCategoryChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    return tags.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
        {tags}
        {tags.length > 0 && (
          <Chip
            label="Clear all"
            size="small"
            variant="outlined"
            onClick={handleReset}
            deleteIcon={<ResetIcon />}
            sx={{ mb: 1 }}
          />
        )}
      </Box>
    ) : null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 3,
      }}
    >
      {/* 메인 검색바 */}
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 8, md: 10 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <OutlinedInput
              placeholder="Search by template key or name..."
              value={formValues.keyword || ""}
              onChange={(event) => {
                handleKeywordChange(event.target.value);
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color={searchFocused ? "primary" : "action"} />
                </InputAdornment>
              }
              sx={{
                transition: "all 0.2s",
                boxShadow: searchFocused ? 1 : 0,
                "&.Mui-focused": {
                  borderColor: "primary.main",
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Advanced filter options">
            <IconButton
              color="primary"
              onClick={handleOpenFilters}
              aria-describedby={id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                flex: 1,
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            sx={{ flex: 3 }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {/* 선택된 필터 태그 */}
      {renderFilterTags()}

      {/* 고급 필터 팝오버 */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 320,
            p: 2,
            mt: 1,
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Advanced Filters
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Category
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.category || ""}
              onChange={(event) => {
                handleCategoryChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="All Categories" />
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  <ListItemText primary={category} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            startIcon={<ResetIcon />}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            disableElevation
            size="small"
            onClick={handleApplyFilters}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </Paper>
  );
};









